/* eslint-disable no-restricted-imports */
import React, {useMemo} from 'react'
import styles from './Logo.module.scss'
import {useUIGameEndContext} from '../../../../contexts/UIGameEndContext.tsx'
import {type TranslationObject} from '@colonist/i18n'
import {T} from '@colonist/ui'
import clsx from 'clsx'
import {FADE_IN_ANIMATION_DELAYS} from '../../constants.ts'
import {turnSecondsInToTimerFormat} from '../../../../../../../code/client/shared/scripts/ui_utils/ClientUtils.ts'
import {getLobbyImagePath} from '../../../../../../../code/client/shared/scripts/ui_utils/Utils.ts'
import {LobbyImage, ModeSettingEnum} from '@colonist/types'
import {FadeInContainer} from '../../../../common/FadeInContainer/index.ts'
import {useGameStore} from '../../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../../store/selectors/usePlayerValidators.ts'

export function Logo({disableAnimation}: {disableAnimation: boolean}) {
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const {gameEndData, isMyColorWinner, currentPlayerEndgameState, onClickHome} = useUIGameEndContext()
    const {playerValidators} = usePlayerValidators()
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)

    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
    const totalPlayers = playerValidators.length

    const endGameText: TranslationObject = useMemo(() => {
        if(isBeginnerMode && !isMyColorWinner) {
            const beginnerModeLostText: TranslationObject = {key: 'strings:game.beginnerMode.opponentWon'}
            return beginnerModeLostText
        }

        if(isMyColorSpectator) return {key: 'strings:game.endGame.rank.spectator'}

        const playerRank = currentPlayerEndgameState?.rank
        if(playerRank == null) return {key: 'strings:game.endGame.rank.wellPlayed'}

        const averageRank = Math.min(3, Math.floor(totalPlayers / 2))
        const maxRankToShow = totalPlayers <= 3 ? 1 : averageRank
        const shouldShowRank = playerRank <= maxRankToShow

        if(!shouldShowRank) {
            return {key: 'strings:game.endGame.rank.wellPlayed'}
        }

        switch(playerRank) {
            case 1: return {key: 'strings:game.endGame.rank.1st'}
            case 2: return {key: 'strings:game.endGame.rank.2nd'}
            case 3: return {key: 'strings:game.endGame.rank.3rd'}
            default: return {key: 'strings:game.endGame.rank.wellPlayed'}
        }
    }, [currentPlayerEndgameState, isMyColorWinner, isBeginnerMode, isMyColorSpectator, totalPlayers])

    const timeTurnsText = useMemo(() => {
        if(isBeginnerMode && !isMyColorWinner) {
            const beginnerModeLostText: TranslationObject = {key: 'strings:game.beginnerMode.lost'}
            return beginnerModeLostText
        }
        const text: TranslationObject = {key: 'strings:game.endGame.generalStats', options: {
            timeString: turnSecondsInToTimerFormat(gameEndData.endGameState.gameDurationInMS / 1000),
            turnCount: String(gameEndData.endGameState.totalTurnCount),
        }}
        return text
    }, [gameEndData, isMyColorWinner, isBeginnerMode])

    return (
        <FadeInContainer
            animationDelayMs={FADE_IN_ANIMATION_DELAYS.logo}
            animationDurationMs={FADE_IN_ANIMATION_DELAYS.transitionDuration}
            className={clsx(styles.logoContainer)}
            disableAnimation={disableAnimation}
        >
            <img className={styles.logo} onClick={onClickHome} src={getLobbyImagePath(LobbyImage.Logo)} />
            <div className={styles.heading}>
                <T id={endGameText}/>
            </div>
            <div className={clsx(styles.timeTurns)}>
                <T id={timeTurnsText}/>
            </div>
        </FadeInContainer>
    )
}
